import {
  IonHeader,
  IonToolbar,
  IonButton,
  IonCard,
  IonContent,
  IonItem,
  IonIcon,
  IonLabel,
  IonBadge,
  IonPage,
  IonText,
  IonFab, 
  IonFabButton, 
  IonList,
  IonImg,
  IonFabList,
  IonListHeader,
  IonSkeletonText,
  IonThumbnail,
  IonRefresher, 
  IonRefresherContent,
  useIonAlert,
  useIonModal,
  IonCardContent,
  IonRow,
  IonCol,
  IonInput,
  IonTextarea,
  useIonLoading,
  IonSelect,
  IonSelectOption,
  IonCardHeader,
  useIonToast,
  IonGrid
} from '@ionic/react';
import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { RefresherEventDetail } from '@ionic/core';
import { chevronDownCircleOutline, arrowBackOutline, menuSharp, locateOutline, cashOutline, checkmarkCircleOutline, closeCircleOutline, bookOutline, receiptOutline, calendarNumberOutline, cameraOutline, closeOutline, trashOutline, addCircleOutline, albumsOutline, warningOutline, lockClosedOutline} from "ionicons/icons";
import { RestApi } from '../../services/Api';
import Map from '../Map';
import HistoryBill from "./History";
import { Bill, CollectProps } from '../../models/Modal';
import { useTranslation } from 'react-i18next';
import { getNameDate, Rupiah, useConstructor } from '../../services/Library';
import SelectOption from '../input/SelectOption';
import { usePhotoGallery } from '../../services/Camera';
import { CameraSource } from '@capacitor/camera';
import { Capacitor, Plugins } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { useSelector } from 'react-redux';
import { billDataCollectReducer } from "../../data/state/BillDataCollectReducer";
import { CollectionEntry, InvoiceCollectItem } from '../../models/Data';
import './Detail.scss';

const Detail: React.FC<Bill> = (props) => {
  const [alert] = useIonAlert();
  const { t } = useTranslation(["general"]);
  const [bill, setBill] = useState<any>([]);
  const [customer, setCustomer] = useState<any>(null);
  const [loaded, setLoaded] = useState<boolean>(false);
  const [settingsMaxOverLessAmount, setSettingsMaxOverLessAmount] = useState<Number>(0);
  const [address, setAddress] = useState<any>();
  const [baseUrl, setBaseUrl] = useState<string>("");
  const user = useSelector((state: any) => state.user.value);
  const [image, setImage] = useState<string>("");
  const collectSum = (billdet: any) =>
    billdet?.billdet_collect?.length
      ? billdet.billdet_collect.reduce(
          (sum: number, c: any) => sum + Number(c.billdet_collect_amount ?? 0),
          0
        )
      : 0;

  const billTotals = useMemo(() => {
    const totals = {
      collect: 0,
      payment: 0,
      allocate: 0,
      owing: 0,
      bill: 0,
      amount: 0,
      srec: 0,
    };
    if (!bill?.billdet || !Array.isArray(bill.billdet)) return totals;
    return bill.billdet.reduce(
      (acc: any, det: any) => ({
        collect: acc.collect + collectSum(det),
        payment: acc.payment + Number(det.billdet_si_total_payment ?? 0),
        allocate: acc.allocate + Number(det.billdet_si_total_allocate ?? 0),
        owing: acc.owing + Number(det.billdet_si_total_owing ?? 0),
        bill: acc.bill + Number(det.billdet_si_total_bill ?? 0),
        amount: acc.amount + Number(det.billdet_si_total_amount ?? 0),
        srec: acc.srec + Number(det.billdet_srec_amount ?? 0),
      }),
      totals
    );
  }, [bill]);

  const [presentCollect, dismissCollect] = useIonModal(ModalCollect, {
    customer: customer,
    bill: bill,
    idVisit: props.idVisit,
    maxOverLessAmount: settingsMaxOverLessAmount,
    onDismissDetail: (e: any) => {
      if (e) {
        getBillDetail(props.detailBill.bill_id, props.detailBill.customer_id);
      }
      dismissCollect();
    },
  });

  const [presentCustomerAddrresModal, dismissCustomerAddressModal] =
    useIonModal(SelectOption, {
      title: `${t("address")} ${t("module.customer")}`,
      items: customer?.customer_address?.map((value: any) => ({
        value: value.customer_address_id,
        text: `${value.customer_address_name}, ${value.customer_address}`,
      })),
      selectedItems: [],
      onSelectionCancel: () => dismissCustomerAddressModal(),
      onSelectionChange: (selected: string[]) => {
        addressSelectionChanged(selected);
        dismissCustomerAddressModal();
      },
      multiple: false,
    });

  const getBillDetail = async (id: string, idCust: string) => {
    if (!id) {
      props.onDismissDetail();
      return;
    }
    setLoaded(false);
    try {
      const res = await RestApi(`bill/detail-bill/${id}/${idCust}`, "get", {}, "application/json");
      if (res.data.code === 200) {
        setBill(res.data.data.bill);
        setCustomer(res.data.data.customer);
        setSettingsMaxOverLessAmount(
          Number(res.data.data.settings_max_over_less_amount??0)
        );
        setLoaded(true);
      } else {
        props.onDismissDetail();
        massageAlert("error", res.data.message);
      }
    } catch (err: any) {
      props.onDismissDetail();
      massageAlert("error", err.message);
    }
  };

  const massageAlert = async (status: string, body: string) => {
    alert({
      header: t(status),
      message: body,
      cssClass: `header-alert-${status}`,
      buttons: [{ text: t('yes'), handler: (d: any) => {} }],
    });
  };

  const doRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    getBillDetail(bill.bill_id, customer?.customer_id);
    event.detail.complete();
  };

  const handleDismissMap = (e: any) => {
    getBillDetail(props.detailBill.bill_id, props.detailBill.customer_id);
    dismissMap();
  };

  const handleDismissHistoryBill = (e: any) => {
    dismissHistoryBill();
  };

  const [presentMap, dismissMap] = useIonModal(Map, {
    customer: {
      customer_id: customer?.customer_id,
      customer_name: customer?.customer_name,
    },
    type: "customer",
    visit: null,
    address: address,
    bill: [],
    onDismissDetail: handleDismissMap,
  });

  const [presentHistoryBill, dismissHistoryBill] = useIonModal(HistoryBill, {
    idCustomer: customer?.customer_id,
    onDismissDetail: handleDismissHistoryBill,
  });

  // Modal for showing full-size bill image
  interface BillImagePreviewProps {
    image: string;
    onDismiss: () => void;
  }

  const BillImagePreview: React.FC<BillImagePreviewProps> = ({
    image,
    onDismiss,
  }) => (
    <IonPage>
      <IonHeader>
        <IonToolbar class="toolbar-detail-bill ion-no-padding">
          <IonButton
            color="light"
            slot="start"
            fill="clear"
            onClick={onDismiss}
          >
            <IonIcon color="light" slot="start" icon={arrowBackOutline} />
          </IonButton>
          <IonItem lines="none" className="ion-no-padding">
            <IonLabel color="light" className="ion-no-padding">
              <h4 className="ion-no-padding ion-no-margin">
                <strong>
                  {t("image")} {t("collect")}
                </strong>
              </h4>
            </IonLabel>
          </IonItem>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonCard className="ion-no-margin">
          <IonCardContent className="ion-no-padding">
            <IonImg
              src={image}
              alt={image}
              style={{
                width: "100%",
                height: "auto",
                maxHeight: "80vh",
                objectFit: "contain",
                display: "block",
                backgroundColor: "#f8f9fa",
              }}
            />
          </IonCardContent>
        </IonCard>
      </IonContent>
    </IonPage>
  );

  const [presentBillImage, dismissBillImage] = useIonModal(BillImagePreview, {
    image: image,
    onDismiss: () => dismissBillImage(),
  });

  const addressSelectionChanged = (idAddress: string[]) => {
    setAddress(
      customer?.customer_address.find(
        (val: any) => val.customer_address_id === idAddress
      )
    );
    presentMap({
      cssClass: "modal-fullscreen",
    });
    return;
  };

  useConstructor(() => {
    getBillDetail(props.detailBill.bill_id, props.detailBill.customer_id);
    (async () => {
      const { value } = await Preferences.get({ key: "url" });
      setBaseUrl(value || "");
    })();
  });

  useEffect(() => {
  }, [bill, customer]);

  return (
    <IonPage>
      <IonHeader className="ion-no-padding ion-no-margin">
        <IonToolbar class="toolbar-detail-bill ion-no-padding ion-no-margin">
          <IonButton
            color="light"
            slot="start"
            fill="clear"
            onClick={() => props.onDismissDetail()}
          >
            <IonIcon color="light" slot="start" icon={arrowBackOutline} />
          </IonButton>
          <IonItem className="ion-no-padding ion-no-margin" lines="none">
            <IonText className="ion-no-padding ion-text-nowrap" color="light">
              <p className="ion-no-padding ion-no-margin">
                <strong>
                  {customer?.customer_name} ({customer?.customer_title})
                </strong>
              </p>
            </IonText>
            <IonBadge slot="end" color="dark">
              {customer?.customer_id}
            </IonBadge>
          </IonItem>
        </IonToolbar>
      </IonHeader>
      <IonContent className="detail-content">
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={`${t("loading")}`}
            refreshingSpinner="circles"
            refreshingText={`${t("loading")}`}
          ></IonRefresherContent>
        </IonRefresher>
        {!loaded && (
          <IonList>
            <IonListHeader>
              <IonSkeletonText
                animated={true}
                style={{ width: "80px" }}
              ></IonSkeletonText>
            </IonListHeader>
            <IonItem>
              <IonThumbnail slot="start">
                <IonSkeletonText animated={true}></IonSkeletonText>
              </IonThumbnail>
              <IonLabel>
                <h3>
                  <IonSkeletonText
                    animated={true}
                    style={{ width: "80%" }}
                  ></IonSkeletonText>
                </h3>
                <p className="ion-no-padding ion-no-margin">
                  <IonSkeletonText
                    animated={true}
                    style={{ width: "60%" }}
                  ></IonSkeletonText>
                </p>
                <p className="ion-no-padding ion-no-margin">
                  <IonSkeletonText
                    animated={true}
                    style={{ width: "30%" }}
                  ></IonSkeletonText>
                </p>
              </IonLabel>
            </IonItem>
          </IonList>
        )}

        {loaded ? (
          <>
            <IonCard>
              <IonCardContent className="ion-no-padding ion-no-margin">
                <IonGrid className="ion-no-padding ion-no-magrin">
                  <IonRow className="ion-no-padding ion-no-margin">
                    <IonCol size="12" className="ion-no-padding ion-no-margin">
                      <IonCard
                        className="ion-no-padding ion-no-margin"
                        color={"medium"}
                      >
                        <IonCardContent
                          className="ion-no-padding ion-no-margin"
                          color={"medium"}
                        >
                          <IonItem color={"medium"}>
                            <IonText className="ion-no-padding ion-no-margin">
                              <h4 className="ion-no-padding ion-no-margin">
                                <strong>{bill.bill_collector_name}</strong>
                              </h4>
                              <p className="ion-no-padding ion-no-margin">
                                No. {t("collect")} <br />
                                {bill.bill_id}
                              </p>
                            </IonText>
                            <IonText
                              className="ion-no-padding ion-no-margin ion-text-end"
                              slot="end"
                            >
                              <h4 className="ion-no-padding ion-no-margin">
                                <strong>{getNameDate(bill.bill_date)}</strong>
                              </h4>
                              <p className="ion-no-padding ion-no-margin">
                                Total {t("invoice")} : <br />
                                {Rupiah(billTotals.amount)}
                              </p>
                            </IonText>
                          </IonItem>
                          <IonItem color={"medium"}>
                            <IonText className="ion-no-padding ion-no-margin">
                              <p className="ion-no-padding ion-no-margin">
                                Total {t("receipt")} : <br />
                                {Rupiah(billTotals.srec)}
                              </p>
                              <p className="ion-no-padding ion-no-margin">
                                Sisa {t("owing")} <br />{" "}
                                {Rupiah(billTotals.owing)}
                              </p>
                            </IonText>
                            <IonText
                              slot="end"
                              className="ion-no-padding ion-text-end"
                            >
                              <p className="ion-no-padding ion-no-margin">
                                Total {t("payment")}
                                <br /> {Rupiah(billTotals.payment)}
                              </p>
                              <p className="ion-no-padding ion-no-margin">
                                Total {t("collect")} <br />
                                {Rupiah(billTotals.collect)}
                              </p>
                            </IonText>
                          </IonItem>
                        </IonCardContent>
                      </IonCard>
                    </IonCol>
                    <IonCol size="12" className="ion-no-padding ion-no-margin">
                      <IonCard>
                        <IonText
                          style={{ backgroundColor: "#f4f5f8" }}
                          color={"dark"}
                          className="ion-no-padding ion-no-margin"
                        >
                          <strong>
                            &nbsp;{`${t("list")} ${t("invoice")}`}
                          </strong>
                        </IonText>
                        {bill.billdet.map((value: any, key: any) => (
                          <IonCol
                            size="12"
                            key={key}
                            className="ion-no-padding ion-no-margin"
                          >
                            <IonCard className="ion-no-padding ion-no-margin ion-margin-bottom">
                              <IonCardHeader className="ion-no-padding ion-no-margin">
                                <IonItem
                                  className="ion-no-padding ion-no-margin"
                                  color="light"
                                >
                                  <IonIcon
                                    size="small"
                                    className="ion-no-padding ion-no-margin"
                                    icon={receiptOutline}
                                    style={{ marginRight: 8 }}
                                  />
                                  <IonLabel
                                    className="ion-no-padding ion-no-margin"
                                    style={{ fontWeight: 600 }}
                                  >
                                    {value.billdet_si_id}
                                  </IonLabel>
                                  <IonText
                                    slot="end"
                                    className="ion-no-padding ion-no-margin ion-text-end"
                                    style={{ fontSize: "0.95em" }}
                                  >
                                    <IonIcon
                                      size="small"
                                      style={{
                                        marginRight: "2px",
                                        verticalAlign: "middle",
                                      }}
                                      icon={calendarNumberOutline}
                                    />
                                    <strong>
                                      &nbsp;
                                      {Number(value.billdet_si_overdue_age)}
                                      &nbsp;{t("day")}
                                    </strong>
                                  </IonText>
                                </IonItem>
                                <IonText className="ion-no-padding ion-no-margin ion-padding-end">
                                  <p
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span>{t("date")} </span>
                                    <span
                                      style={{
                                        marginLeft: 8,
                                        textAlign: "right",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {getNameDate(value.billdet_si_date)}
                                    </span>
                                  </p>
                                  <p
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span>{t("due_date")} </span>
                                    <span
                                      style={{
                                        marginLeft: 8,
                                        textAlign: "right",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {getNameDate(value.billdet_si_due_date)}
                                    </span>
                                  </p>
                                  <p
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span>{t("salesman")} </span>
                                    <span
                                      style={{
                                        marginLeft: 8,
                                        textAlign: "right",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {value.si_salesman_name ?? "-"}
                                    </span>
                                  </p>
                                  <hr />
                                  <p
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span>Total {t("invoice")} </span>
                                    <span
                                      style={{
                                        marginLeft: 8,
                                        textAlign: "right",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {Rupiah(
                                        value.billdet_si_total_amount ?? 0
                                      )}
                                    </span>
                                  </p>
                                  <p
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span>Total {t("payment")} </span>
                                    <span
                                      style={{
                                        marginLeft: 8,
                                        textAlign: "right",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {Rupiah(
                                        (value.billdet_si_total_payment ?? 0) +
                                          (value.billdet_si_total_allocate ?? 0)
                                      )}
                                    </span>
                                  </p>
                                  <p
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span>Sisa {t("owing")} </span>
                                    <span
                                      style={{
                                        marginLeft: 8,
                                        textAlign: "right",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {Rupiah(
                                        value.billdet_si_total_owing ?? 0
                                      )}
                                    </span>
                                  </p>
                                  <p
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span>{t("receipt")}</span>
                                    <span
                                      style={{
                                        marginLeft: 8,
                                        textAlign: "right",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {Rupiah(value.billdet_srec_amount)}
                                    </span>
                                  </p>
                                  <p
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      alignItems: "flex-start",
                                    }}
                                  >
                                    <span>{t("collect")} </span>
                                    <span
                                      style={{
                                        marginLeft: 8,
                                        textAlign: "right",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {Rupiah(collectSum(value))}
                                    </span>
                                  </p>
                                </IonText>
                                <IonItem>
                                  <div
                                    className="ion-no-padding ion-no-margin"
                                    style={{
                                      width: "100%",
                                      minHeight: "56px",
                                      background: "#fff",
                                      borderRadius: "8px",
                                      fontSize: "15px",
                                      color: "#333",
                                      wordBreak: "break-word",
                                    }}
                                    dangerouslySetInnerHTML={{
                                      __html: `<small>Catatan</small> <br>${
                                        value.billdet_remark || "-"
                                      }`,
                                    }}
                                  />
                                  <IonText
                                    className="ion-no-padding ion-no-margin ion-text-right"
                                    slot="end"
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: "8px",
                                        alignItems: "flex-end",
                                      }}
                                    >
                                      <IonBadge
                                        color={
                                          value.billdet_is_check
                                            ? "success"
                                            : "medium"
                                        }
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "2px",
                                          padding: "2px 6px",
                                          fontSize: "10px",
                                          height: "18px",
                                          minWidth: "32px",
                                        }}
                                      >
                                        <IonIcon
                                          icon={
                                            value.billdet_is_check
                                              ? checkmarkCircleOutline
                                              : closeCircleOutline
                                          }
                                          size="small"
                                        />
                                        <span style={{ fontSize: "11px" }}>
                                          Check
                                        </span>
                                      </IonBadge>
                                      <IonBadge
                                        color={
                                          value.billdet_image
                                            ? "success"
                                            : "medium"
                                        }
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "2px",
                                          padding: "2px 6px",
                                          fontSize: "10px",
                                          height: "18px",
                                          minWidth: "32px",
                                        }}
                                        onClick={() => {
                                          if (value.billdet_image) {
                                            setImage(
                                              `${baseUrl}/image/bill/${user.company_id}/${value.billdet_image}`
                                            );
                                            presentBillImage();
                                          }
                                        }}
                                      >
                                        <IonIcon
                                          icon={
                                            value.billdet_image
                                              ? checkmarkCircleOutline
                                              : closeCircleOutline
                                          }
                                          size="small"
                                        />
                                        <span style={{ fontSize: "11px" }}>
                                          {t("image")}
                                        </span>
                                      </IonBadge>
                                    </div>
                                  </IonText>
                                </IonItem>
                              </IonCardHeader>
                              <IonCardContent className="ion-no-padding ion-no-margin">
                                <IonText
                                  style={{ backgroundColor: "#f4f5f8" }}
                                  color={"dark"}
                                  className="ion-no-padding ion-no-margin"
                                >
                                  <span className="ion-no-padding ion-no-margin">
                                    <strong>
                                      &nbsp;{`${t("list")} ${t("collect")}`}
                                    </strong>
                                  </span>
                                </IonText>
                                {value.billdet_collect?.length ? (
                                  value.billdet_collect.map(
                                    (value: any, key: any) => (
                                      <IonCard
                                        key={key}
                                        className="ion-no-padding ion-no-margin"
                                      >
                                        <IonItem className="ion-no-padding ion-no-margin">
                                          <IonLabel className="ion-no-padding ion-no-margin ion-padding-start">
                                            <h4>
                                              <strong>
                                                {t("type")} {t("collect")}
                                                {" : "}{" "}
                                                {t(
                                                  value.billdet_collect_type.toLowerCase()
                                                )}
                                              </strong>
                                            </h4>
                                            <h4>Di {t("receive")} : </h4>
                                          </IonLabel>
                                          <IonLabel
                                            className="ion-no-padding ion-no-margin"
                                            slot="end"
                                          >
                                            <p>
                                              {Rupiah(
                                                Number(
                                                  value.billdet_collect_amount ??
                                                    0
                                                )
                                              )}
                                            </p>
                                            <p>
                                              {Rupiah(
                                                Number(
                                                  value.billdet_collect_receive_amount ??
                                                    0
                                                )
                                              )}
                                            </p>
                                          </IonLabel>
                                        </IonItem>
                                        <IonItem lines="none">
                                          <IonLabel className="ion-no-padding ion-no-margin">
                                            <p
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                              }}
                                            >
                                              <span>{t("collect")} Oleh</span>
                                              <span
                                                style={{
                                                  marginLeft: 8,
                                                  textAlign: "right",
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {value.create_by?.user_name}
                                              </span>
                                            </p>
                                            <p
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "flex-start",
                                              }}
                                            >
                                              <span>{t("date")} </span>
                                              <span
                                                style={{
                                                  marginLeft: 8,
                                                  textAlign: "right",
                                                  fontWeight: 600,
                                                }}
                                              >
                                                {getNameDate(
                                                  value.billdet_collect_create_at,
                                                  true
                                                )}
                                              </span>
                                            </p>
                                            {value.update_by?.user_name && (
                                              <p
                                                style={{
                                                  display: "flex",
                                                  justifyContent: "space-between",
                                                  alignItems: "flex-start",
                                                }}
                                              >
                                                <span>{t("receive")} Oleh</span>
                                                <span
                                                  style={{
                                                    marginLeft: 8,
                                                    textAlign: "right",
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {value.update_by.user_name}
                                                </span>
                                              </p>
                                            )}
                                            {value.billdet_collect_update_at && (
                                              <p
                                                style={{
                                                  display: "flex",
                                                  justifyContent: "space-between",
                                                  alignItems: "flex-start",
                                                }}
                                              >
                                                <span>{t("date")}</span>
                                                <span
                                                  style={{
                                                    marginLeft: 8,
                                                    textAlign: "right",
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  {getNameDate(
                                                    value.billdet_collect_update_at,
                                                    true
                                                  )}
                                                </span>
                                              </p>
                                            )}
                                          </IonLabel>
                                        </IonItem>
                                      </IonCard>
                                    )
                                  )
                                ) : (
                                  <IonText className="ion-text-center">
                                    <p className="ion-no-padding ion-no-margin">{`${t(
                                      "collect"
                                    )} ${t("not-found")}`}</p>
                                  </IonText>
                                )}
                              </IonCardContent>
                            </IonCard>
                          </IonCol>
                        ))}
                      </IonCard>
                    </IonCol>
                  </IonRow>
                </IonGrid>
                <IonItem>
                  <IonText className="ion-no-padding ion-no-margin">
                    <h4>
                      <strong>
                        Check By{" "}
                        <IonIcon
                          color={bill.bill_check_by ? "success" : "danger"}
                          icon={
                            bill.bill_check_by
                              ? checkmarkCircleOutline
                              : closeCircleOutline
                          }
                        />
                      </strong>
                    </h4>
                  </IonText>
                  <IonText slot="end" className="ion-text-right">
                    <span>{bill.bill_check_by}</span>
                    <span>
                      {bill.bill_check_at
                        ? getNameDate(bill.bill_check_at, true)
                        : "-"}
                    </span>
                  </IonText>
                </IonItem>
              </IonCardContent>
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
            </IonCard>
          </>
        ) : (
          <IonImg className="img-empty" src={"assets/img/search-empty.png"} />
        )}
      </IonContent>
      {loaded && (
        <>
          {!bill.bill_is_closed ? (
            <IonFab horizontal="center" vertical="bottom">
              <IonButton
                color="danger"
                onClick={() => {
                  presentCollect({
                    cssClass: "modal-fullscreen",
                  });
                }}
                style={{
                  width: "60vw",
                  maxWidth: 600,
                  borderRadius: 60,
                  boxShadow: "0 4px 16px rgba(0,0,0,0.11)",
                }}
              >
                <span
                  style={{ fontSize: "1em", fontWeight: 500, marginRight: 8 }}
                >
                  {t("collect")}
                </span>
                <IonIcon size="small" icon={cashOutline} />
              </IonButton>
            </IonFab>
          ) : (
            ""
          )}
          <IonFab horizontal="end" vertical="bottom">
            <IonFabButton className="fab-up" size="small">
              <IonIcon size="small" icon={menuSharp} />
            </IonFabButton>
            <IonFabList side="top">
              <IonFabButton
                data-desc="Lokasi"
                onClick={() => {
                  presentCustomerAddrresModal({
                    initialBreakpoint: 0.9,
                    breakpoints: [0, 0.9, 1.0],
                    handleBehavior: "cycle",
                    backdropDismiss: true,
                    id: "modal-customer-address",
                    onWillDismiss: async () => {
                      const modalElement = document.getElementById(
                        "modal-customer-address"
                      ) as HTMLIonModalElement;
                      if (modalElement) {
                        modalElement?.dismiss();
                      }
                    },
                  });
                }}
              >
                <IonIcon icon={locateOutline} />
              </IonFabButton>

              <IonFabButton
                data-desc={`History ${t("module.bill")}`}
                onClick={() => {
                  presentHistoryBill({
                    cssClass: "modal-fullscreen",
                  });
                }}
              >
                <IonIcon size="medium" icon={bookOutline} />
              </IonFabButton>
            </IonFabList>
          </IonFab>
        </>
      )}
    </IonPage>
  );
};

const ModalCollect: React.FC<CollectProps> = ({
  bill,
  customer,
  idVisit,
  maxOverLessAmount,
  onDismissDetail,
}) => {
  const [presentToast] = useIonToast();
  const { t } = useTranslation(["general"]);
  const [presentAlert] = useIonAlert();
  const [presentLoading, dismissLoading] = useIonLoading();
  const user = useSelector((state: any) => state.user.value);
  const { deletePhoto, photos, takePhoto } = usePhotoGallery();
  const [baseUrl, setBaseUrl] = useState<string>("");
  const [collectDelete, setCollectDelete] = useState<CollectionEntry[]>([]);
  const collectSum = (collect: any) => {
    return collect && Array.isArray(collect)
      ? collect.reduce((sum: number, c: any) => sum + Number(c.amount ?? 0), 0)
      : 0;
  };

  // Initialize billDataCollect state
  const initialBillData: InvoiceCollectItem[] = bill.billdet.map(
    (invoice: any) => ({
      invoice,
      remark: invoice.billdet_remark ?? "",
      image: invoice.billdet_image ?? null,
      collect: invoice.billdet_collect.length
        ? invoice.billdet_collect.map((collect: any) => ({
            id: collect.billdet_collect_id,
            amount: Number(collect.billdet_collect_amount),
            type: collect.billdet_collect_type,
          }))
        : [{ id: 0, amount: 0, type: "CASH" }],
    })
  );

  const [billDataCollect, dispatch] = useReducer(
    billDataCollectReducer,
    initialBillData
  );

  // Alert helper
  const showAlert = async (status: "success" | "error", body: string) => {
    presentAlert({
      header: t(status),
      message: body,
      cssClass: `header-alert-${status}`,
      buttons: [{ text: t("yes") }],
    });
  };

  // Handle image capture
  const handleImageCapture = async (index: number) => {
    try {
      const photo = await takePhoto(CameraSource.Camera);
      if (photo?.webviewPath) {
        dispatch({
          type: "UPDATE_FIELD",
          payload: { index, fieldPath: "image", value: photo },
        });
      }
    } catch (error) {
      console.error("Error taking photo:", error);
      showAlert("error", "Failed to capture image");
    }
  };

  // Select image source (camera or replace)
  const selectImageSource = async (index: number) => {
    const currentImage = billDataCollect[index].image;
    const hasImage = currentImage && currentImage !== "undefined";
    const isDisabled =
      !billDataCollect[index]?.invoice?.billdet_collect?.some(
        (c: any) => Number(c.billdet_collect_receive_amount) > 0
      );

    if (isDisabled) {
      presentToast({
        message: t("Input disabled pada invoice ini."),
        duration: 1500,
        color: "danger",
        position: "middle",
      });
      return;
    }

    if (hasImage) {
      presentAlert({
        message: "Yakin ingin ubah foto ini?",
        backdropDismiss: false,
        buttons: [
          { text: t("no"), cssClass: "alert-button-cancel", role: "cancel" },
          {
            text: t("yes"),
            cssClass: "alert-button-confirm",
            role: "confirm",
            handler: () => {
              photos.forEach(deletePhoto);
              handleImageCapture(index);
            },
          },
        ],
      });
    } else {
      await handleImageCapture(index);
    }
  };

  // Update nested field in invoice data
  const updateInvoiceData = (index: number, fieldPath: string, value: any) => {
    const isDisabled =
      !!billDataCollect[index]?.invoice?.billdet_collect?.some(
        (c: any) => Number(c.billdet_collect_receive_amount) > 0
      );
    if (isDisabled) return;
    dispatch({ type: "UPDATE_FIELD", payload: { index, fieldPath, value } });
  };

  // Add new collection entry
  const addCollectionEntry = (index: number) => {
    const isDisabled =
      !!billDataCollect[index]?.invoice?.billdet_collect?.some(
        (c: any) => Number(c.billdet_collect_receive_amount) > 0
      );
    if (isDisabled) return;
    dispatch({ type: "ADD_COLLECTION_ENTRY", payload: { index } });
  };

  // Delete collection entry
  const deleteCollectionEntry = (
    invoiceIndex: number,
    collectIndex: number
  ) => {
    const isDisabled =
      !!billDataCollect[invoiceIndex]?.invoice?.billdet_collect?.some(
        (c: any) => Number(c.billdet_collect_receive_amount) > 0
      );
    if (isDisabled) return;
    const deleted = billDataCollect[invoiceIndex].collect[collectIndex];
    setCollectDelete((prev) => [...prev, deleted]);
    dispatch({
      type: "DELETE_COLLECTION_ENTRY",
      payload: { invoiceIndex, collectIndex },
    });
  };

  // Split payment across invoices
  const splitTotalInvoice = () => {
    const anyDisabled = billDataCollect.some((inv) =>
      inv.invoice?.billdet_collect?.some(
        (c: any) => Number(c.billdet_collect_receive_amount) > 0
      )
    );
    if (anyDisabled) {
      presentToast({
        message: t("Split disabled karena ada invoice yang sudah di-check"),
        duration: 1500,
        color: "danger",
        position: "middle",
      });
      return;
    }
    const totalCollected = billDataCollect.reduce((sum, item) => {
      return sum + (item.collect?.reduce((a: any, c: { amount: any; }) => a + c.amount, 0) ?? 0);
    }, 0);

    if (totalCollected !== 0) {
      showAlert(
        "error",
        "Fungsi Split hanya bisa digunakan ketika daftar menagih kosong"
      );
      return;
    }

    const maxInputCollect =
      billDataCollect.reduce(
        (sum, item) => sum + Number(item.invoice.billdet_si_total_owing ?? 0),
        0
      ) +
      maxOverLessAmount * billDataCollect.length;

    presentAlert({
      header: `Total Split ${t("collect")}`,
      cssClass: "header-alert",
      inputs: [
        {
          placeholder: `Jumlah Max: ${Rupiah(maxInputCollect)}`,
          type: "number",
          name: "totalSplit",
        },
      ],
      buttons: [
        { text: "Cancel", role: "cancel", cssClass: "alert-button-cancel" },
        {
          text: t("yes"),
          role: "confirm",
          cssClass: "alert-button-confirm",
          handler: (data) => {
            const rawValue = (data as any)?.totalSplit;
            const inputVal = Number(rawValue);

            if (!inputVal) {
              presentToast({
                message: t("required"),
                duration: 1500,
                icon: warningOutline,
                position: "middle",
              });
              return false; // keep alert open
            }

            if (inputVal > maxInputCollect) {
              presentToast({
                message: `Jumlah ${t("collect")} Max: ${Rupiah(
                  maxInputCollect
                )}`,
                position: "middle",
                duration: 1500,
                icon: warningOutline,
              });
              return false;
            }

            let remaining = inputVal;
            const newBillData = [...billDataCollect];

            for (let i = 0; i < newBillData.length; i++) {
              const invoice = newBillData[i];
        
              if (
                invoice.invoice.billdet_collect.some(
                  (c: any) => Number(c.billdet_collect_receive_amount) > 0
                )
              )
                continue; // Skip disabled invoices

              const totalOwing = Number(
                invoice.invoice.billdet_si_total_owing ?? 0
              );
              let amountToSet = Math.min(remaining, totalOwing);
              remaining -= amountToSet;

              // If last invoice, assign any leftover
              if (i === newBillData.length - 1) {
                amountToSet += remaining;
                remaining = 0;
              }

              // Update all collect entries for this invoice
              invoice.collect.forEach((_: any, collectIndex: number) => {
                updateInvoiceData(
                  i,
                  `collect[${collectIndex}].amount`,
                  amountToSet
                );
              });

              if (remaining <= 0) break;
            }
            return true;
          },
        },
      ],
    });
  };

  // Rupiah formatting utilities
  const formatRupiah = (value: number | string): string => {
    const num =
      typeof value === "string"
        ? parseFloat(value.replace(/[^\d.]/g, ""))
        : value;
    return isNaN(num as number)
      ? "0"
      : (num as number).toLocaleString("id-ID", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
  };

  const handleRupiahInputChange = (
    e: CustomEvent,
    index: number,
    collectIndex: number,
    invoice: InvoiceCollectItem
  ) => {
    const isDisabled =
      !!invoice.invoice.billdet_collect?.some(
        (c: any) => Number(c.billdet_collect_receive_amount) > 0
      );
    if (isDisabled) return;
    const value = e.detail.value ?? "";
    const cleanValue = value.replace(/[^\d]/g, "");
    let inputVal = Number(cleanValue);

    const currentTotal = invoice.collect.reduce(
      (sum, c, idx) => sum + (idx === collectIndex ? 0 : c.amount),
      0
    );
    const maxAllowed =
      Number(invoice.invoice.billdet_si_total_owing) -
      currentTotal +
      maxOverLessAmount;

    if (inputVal > maxAllowed) {
      showAlert("error", `Jumlah ${t("collect")} Max: ${Rupiah(maxAllowed)}`);
      inputVal = maxAllowed;
    }

    updateInvoiceData(index, `collect[${collectIndex}].amount`, inputVal);
  };

  const handleRupiahInputDisplay = (
    value: number | null | undefined
  ): string => {
    return value != null ? formatRupiah(value) : "";
  };

  // Submit handler
  const handleSubmit = async () => {
    try {
      await presentLoading({ message: t("loading") });
      const formData = new FormData();

      for (let i = 0; i < billDataCollect.length; i++) {
        const item = billDataCollect[i];
        formData.append(`collects[${i}][si_id]`, item.invoice.si_id);
        formData.append(`collects[${i}][remark]`, item.remark);

        if (item.image?.webviewPath) {
          const response = await fetch(item.image.webviewPath);
          const blob = await response.blob();
          formData.append(`image-${i}`, blob);
        }

        item.collect.forEach((collect: any, j: number) => {
          formData.append(
            `collects[${i}][collect][${j}][id]`,
            collect.id.toString()
          );
          formData.append(
            `collects[${i}][collect][${j}][amount]`,
            collect.amount.toString()
          );
          formData.append(`collects[${i}][collect][${j}][type]`, collect.type);
        });
      }

      formData.append("count", billDataCollect.length.toString());
      formData.append("visit_id", idVisit ?? "");
      formData.append("customer_id", customer.customer_id);
      formData.append("collectDelete", JSON.stringify(collectDelete));

      const res = await RestApi(
        `bill/collect/${bill.bill_id}`,
        "post",
        formData,
        "multipart/form-data"
      );

      if (res.data.code === 200) {
        photos.forEach(deletePhoto);
        onDismissDetail(true);
        showAlert("success", res.data.message);
      } else {
        showAlert("error", res.data.message);
      }
    } catch (error: any) {
      showAlert("error", error.message || "Terjadi kesalahan");
    } finally {
      dismissLoading();
    }
  };

  // Cleanup photos on unmount / back button
  useConstructor(async () => {
    const { value } = await Preferences.get({ key: "url" });
    setBaseUrl(value || "");

    const cleanupPhotos = () => photos.forEach(deletePhoto);

    if (Capacitor.isNative) {
      const backHandler = Plugins.App.addListener("backButton", cleanupPhotos);
      return () => backHandler.remove();
    } else {
      window.addEventListener("beforeunload", cleanupPhotos);
      return () => window.removeEventListener("beforeunload", cleanupPhotos);
    }
  });

  // Render image helper with modern UI
  const renderImage = (invoice: InvoiceCollectItem, index: number) => {
    const hasImage = invoice.image && invoice.image !== "undefined";

    if (!hasImage) {
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px 20px",
            backgroundColor: "#f8f9fa",
            borderRadius: "8px",
            border: "2px dashed #dee2e6",
            minHeight: "200px",
          }}
        >
          <IonIcon
            icon={cameraOutline}
            size="large"
            style={{ color: "#adb5bd", marginBottom: "12px" }}
          />
          <IonText color="medium">
            <p style={{ margin: 0, fontSize: "14px", fontWeight: 500 }}>
              {t("image")} {t("not-found")}
            </p>
            <p style={{ margin: "4px 0 0 0", fontSize: "12px" }}>
              Tap "Upload {t("image")}" to add image
            </p>
          </IonText>
        </div>
      );
    }

    const imageSrc =
      typeof invoice.image === "object"
        ? invoice.image.webviewPath
        : `${baseUrl}/image/bill/${user.company_id}/${invoice.image}`;

    return (
      <div
        style={{
          position: "relative",
          borderRadius: "8px",
          overflow: "hidden",
          backgroundColor: "#f8f9fa",
          border: "1px solid #e9ecef",
        }}
      >
        <IonImg
          src={imageSrc}
          alt={`Invoice ${invoice.invoice.billdet_si_id} image`}
          style={{
            width: "100%",
            height: "auto",
            display: "block",
            maxHeight: "400px",
            objectFit: "contain",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "8px",
            right: "8px",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            borderRadius: "50%",
            padding: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IonIcon
            icon={checkmarkCircleOutline}
            style={{ color: "#fff", fontSize: "20px" }}
          />
        </div>
      </div>
    );
  };

  // Total collected for display
  const totalCollected = billDataCollect.reduce(
    (sum, item) => sum + (item.collect?.reduce((a: any, c: { amount: any; }) => a + c.amount, 0) ?? 0),
    0
  );

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar class="toolbar-detail-bill ion-no-padding">
          <IonButton
            color="light"
            slot="start"
            fill="clear"
            onClick={() => onDismissDetail(false)}
          >
            <IonIcon color="light" slot="start" icon={closeOutline} />
          </IonButton>
          <IonItem className="ion-no-padding">
            <IonLabel className="ion-no-padding" color="light">
              <h4>
                <strong>
                  Pembayaran, Catatan & Upload Gambar <br /> ({bill.bill_id})
                </strong>
              </h4>
            </IonLabel>
            <IonIcon
              slot="end"
              size="medium"
              color="primary"
              icon={cashOutline}
            />
          </IonItem>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonList>
          {billDataCollect.map((invoice, index) => {
            const isDisabled = !!invoice.invoice.billdet_collect?.some(
              (c: any) => Number(c.billdet_collect_receive_amount) > 0
            );
            return (
              <IonCard key={index}>
                <IonCardHeader className="ion-no-padding ion-no-margin">
                  <IonItem
                    className="ion-no-padding ion-no-margin"
                    color="light"
                  >
                    <IonIcon
                      size="small"
                      className="ion-no-padding ion-no-margin"
                      icon={receiptOutline}
                      style={{ marginRight: 8 }}
                    />
                    <IonLabel
                      className="ion-no-padding ion-no-margin"
                      style={{ fontWeight: 600 }}
                    >
                      {invoice.invoice.billdet_si_id}
                    </IonLabel>
                    <IonText
                      slot="end"
                      className="ion-no-padding ion-no-margin ion-text-end"
                      style={{ fontSize: "0.95em" }}
                    >
                      <IonIcon
                        size="small"
                        style={{
                          marginRight: "2px",
                          verticalAlign: "middle",
                        }}
                        icon={calendarNumberOutline}
                      />
                      <strong>
                        &nbsp;
                        {Number(invoice.invoice.billdet_si_overdue_age)}
                        &nbsp;{t("day")}
                      </strong>
                    </IonText>
                  </IonItem>
                  <IonText className="ion-no-padding ion-no-margin ion-padding-end">
                    <p
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <span>{t("date")} </span>
                      <span
                        style={{
                          marginLeft: 8,
                          textAlign: "right",
                          fontWeight: 600,
                        }}
                      >
                        {getNameDate(invoice.invoice.billdet_si_date)}
                      </span>
                    </p>
                    <p
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <span>{t("due_date")} </span>
                      <span
                        style={{
                          marginLeft: 8,
                          textAlign: "right",
                          fontWeight: 600,
                        }}
                      >
                        {getNameDate(invoice.invoice.billdet_si_due_date)}
                      </span>
                    </p>
                    <p
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <span>{t("salesman")} </span>
                      <span
                        style={{
                          marginLeft: 8,
                          textAlign: "right",
                          fontWeight: 600,
                        }}
                      >
                        {invoice.invoice.si_salesman_name ?? "-"}
                      </span>
                    </p>
                    <hr />
                    <p
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <span>Total {t("invoice")} </span>
                      <span
                        style={{
                          marginLeft: 8,
                          textAlign: "right",
                          fontWeight: 600,
                        }}
                      >
                        {Rupiah(invoice.invoice.billdet_si_total_amount ?? 0)}
                      </span>
                    </p>
                    <p
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <span>Total {t("payment")} </span>
                      <span
                        style={{
                          marginLeft: 8,
                          textAlign: "right",
                          fontWeight: 600,
                        }}
                      >
                        {Rupiah(
                          (invoice.invoice.billdet_si_total_payment ?? 0) +
                            (invoice.invoice.billdet_si_total_allocate ?? 0)
                        )}
                      </span>
                    </p>
                    <p
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <span>Sisa {t("owing")} </span>
                      <span
                        style={{
                          marginLeft: 8,
                          textAlign: "right",
                          fontWeight: 600,
                        }}
                      >
                        {Rupiah(invoice.invoice.billdet_si_total_owing ?? 0)}
                      </span>
                    </p>
                    <p
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <span>{t("receipt")}</span>
                      <span
                        style={{
                          marginLeft: 8,
                          textAlign: "right",
                          fontWeight: 600,
                        }}
                      >
                        {Rupiah(invoice.invoice.billdet_srec_amount)}
                      </span>
                    </p>
                    <p
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <span>{t("collect")} </span>
                      <span
                        style={{
                          marginLeft: 8,
                          textAlign: "right",
                          fontWeight: 600,
                        }}
                      >
                        {Rupiah(collectSum(invoice.collect))}
                      </span>
                    </p>
                  </IonText>
                </IonCardHeader>

                <IonCardContent className="ion-no-padding ion-no-margin">
                  <IonItem className="ion-no-padding ion-no-margin">
                    <IonText color="dark">
                      <strong>&nbsp;{`${t("list")} ${t("collect")}`}</strong>
                    </IonText>
                    <IonBadge
                      color="warning"
                      slot="end"
                      onClick={() => !isDisabled && addCollectionEntry(index)}
                      style={
                        isDisabled
                          ? {
                              opacity: 0.5,
                              pointerEvents: "none",
                              cursor: "not-allowed",
                            }
                          : {}
                      }
                    >
                      <IonIcon size="small" icon={addCircleOutline} />
                    </IonBadge>
                  </IonItem>

                  {invoice.collect.map(
                    (
                      collection: {
                        amount: number | null | undefined;
                        type: unknown;
                      },
                      collectIndex: number
                    ) => (
                      <IonCard key={collectIndex}>
                        <IonRow>
                          <IonCol size="6">
                            <IonInput
                              labelPlacement="floating"
                              label={`Jumlah ${t("collect")}`}
                              type="text"
                              inputmode="numeric"
                              placeholder={`Max: ${Rupiah(
                                Number(invoice.invoice.billdet_si_total_owing) -
                                  invoice.collect.reduce(
                                    (
                                      a: any,
                                      b: { amount: any },
                                      idx: React.Key | null | undefined
                                    ) =>
                                      a + (idx === collectIndex ? 0 : b.amount),
                                    0
                                  ) +
                                  maxOverLessAmount
                              )}`}
                              value={handleRupiahInputDisplay(
                                collection.amount
                              )}
                              onIonInput={(e) =>
                                handleRupiahInputChange(
                                  e,
                                  index,
                                  collectIndex,
                                  invoice
                                )
                              }
                              disabled={isDisabled}
                            />
                          </IonCol>
                          <IonCol size="4">
                            <IonSelect
                              disabled={isDisabled}
                              labelPlacement="floating"
                              label={t("type")}
                              value={collection.type}
                              onIonChange={(e) =>
                                updateInvoiceData(
                                  index,
                                  `collect[${collectIndex}].type`,
                                  e.detail.value
                                )
                              }
                            >
                              <IonSelectOption value="CASH">
                                {t("cash")}
                              </IonSelectOption>
                              <IonSelectOption value="TRANSFER">
                                {t("transfer")}
                              </IonSelectOption>
                              <IonSelectOption value="GIRO">
                                {t("giro")}
                              </IonSelectOption>
                              <IonSelectOption value="DV">
                                {t("dv")}
                              </IonSelectOption>
                            </IonSelect>
                          </IonCol>
                          <IonCol size="2">
                            <IonButton
                              fill="clear"
                              color="danger"
                              size="small"
                              disabled={collectIndex === 0 || isDisabled}
                              onClick={() =>
                                deleteCollectionEntry(index, collectIndex)
                              }
                            >
                              <IonIcon icon={trashOutline} />
                            </IonButton>
                          </IonCol>
                        </IonRow>
                      </IonCard>
                    )
                  )}

                  <IonCard>
                    <IonItem>
                      <IonTextarea
                        labelPlacement="floating"
                        label={t("note")}
                        placeholder={t("note")}
                        value={invoice.remark}
                        onIonInput={(e) =>
                          updateInvoiceData(index, "remark", e.detail.value)
                        }
                        disabled={isDisabled}
                      />
                    </IonItem>
                    <IonItem
                      onClick={() => !isDisabled && selectImageSource(index)}
                      button={!isDisabled}
                      detail={false}
                      style={{
                        cursor: isDisabled ? "not-allowed" : "pointer",
                        opacity: isDisabled ? 0.65 : 1,
                        "--background-hover": "#f8f9fa",
                      }}
                    >
                      <IonIcon
                        icon={cameraOutline}
                        slot="start"
                        color="primary"
                        style={{ fontSize: "24px" }}
                      />
                      <IonLabel>
                        <h3 style={{ fontWeight: 600, margin: 0 }}>
                          {invoice.image && invoice.image !== "undefined"
                            ? "Ubah Foto"
                            : `Upload ${t("image")}`}
                        </h3>
                        <p
                          style={{
                            fontSize: "12px",
                            color: "#6c757d",
                            margin: "4px 0 0 0",
                          }}
                        >
                          {invoice.image && invoice.image !== "undefined"
                            ? "Tap untuk mengganti foto"
                            : "Tap untuk mengambil atau memilih foto"}
                        </p>
                      </IonLabel>
                      {invoice.image && invoice.image !== "undefined" && (
                        <IonBadge color="success" slot="end">
                          <IonIcon icon={checkmarkCircleOutline} size="small" />
                        </IonBadge>
                      )}
                      {isDisabled && (
                        <IonBadge color="medium" slot="end">
                          <IonIcon icon={lockClosedOutline} size="small" />
                        </IonBadge>
                      )}
                    </IonItem>
                    <IonCardContent style={{ padding: "12px" }}>
                      {renderImage(invoice, index)}
                    </IonCardContent>
                  </IonCard>
                </IonCardContent>
              </IonCard>
            );
          })}

          <IonCard>
            <IonItem>
              <IonInput
                labelPlacement="floating"
                label={`Total ${t("collect")} ${t("bill")}`}
                type="text"
                readonly
                value={Rupiah(totalCollected)}
              />
              <IonBadge
                slot="end"
                color="medium"
                onClick={splitTotalInvoice}
                style={
                  billDataCollect.some((inv) =>
                    inv.invoice?.billdet_collect?.some(
                      (c: any) => Number(c.billdet_collect_receive_amount) > 0
                    )
                  )
                    ? {
                        opacity: 0.5,
                        pointerEvents: "none",
                        cursor: "not-allowed",
                      }
                    : {}
                }
              >
                <small>
                  <IonIcon size="small" icon={albumsOutline} /> Split{" "}
                  {t("collect")}
                </small>
              </IonBadge>
            </IonItem>
          </IonCard>
        </IonList>

        <IonButton expand="block" onClick={handleSubmit}>
          {t("submit")}
        </IonButton>
      </IonContent>
    </IonPage>
  );
};
export default Detail;

